import React, { useState, useEffect } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Textarea, Input } from "@heroui/react";

const LandingPageModal = ({ user, orgId, dismissModal }) => {
  const [landingPageText, setLandingPageText] = useState('');
  const [landingPageImage, setLandingPageImage] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Load current landing page data
    const loadLandingPageData = async () => {
      try {
        const response = await fetch(`/api/v1/organizations/${orgId}/landing_page`);
        if (response.ok) {
          const data = await response.json();
          setLandingPageText(data.landing_page_text || '');
          setCurrentImageUrl(data.landing_page_image_url || '');
        }
      } catch (error) {
        console.error('Error loading landing page data:', error);
      }
    };
    
    if (orgId) {
      loadLandingPageData();
    }
  }, [orgId]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Image must be smaller than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      setLandingPageImage(file);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    
    try {
      const formData = new FormData();
      
      // Strip HTML tags from text
      const sanitizedText = landingPageText.replace(/<[^>]*>/g, '');
      formData.append('landing_page_text', sanitizedText);
      
      if (landingPageImage) {
        setUploading(true);
        formData.append('landing_page_image', landingPageImage);
      }

      const response = await fetch(`/api/v1/organizations/${orgId}/landing_page`, {
        method: 'PUT',
        body: formData
      });

      if (response.ok) {
        alert('Landing page updated successfully!');
        dismissModal();
      } else {
        const error = await response.json();
        alert('Error updating landing page: ' + (error.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving landing page:', error);
      alert('Error saving landing page');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={dismissModal} size="2xl">
      <ModalContent>
        <ModalHeader>
          <h3>Organization Landing Page</h3>
        </ModalHeader>
        <ModalBody>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Landing Page Image
            </label>
            {currentImageUrl && (
              <div style={{ marginBottom: '10px' }}>
                <img 
                  src={currentImageUrl} 
                  alt="Current landing page" 
                  style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <p style={{ fontSize: '12px', color: '#666', margin: '5px 0' }}>Current image</p>
              </div>
            )}
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              placeholder="Choose image file (max 5MB)"
            />
            {landingPageImage && (
              <p style={{ fontSize: '12px', color: '#02A0B0', marginTop: '5px' }}>
                New image selected: {landingPageImage.name}
              </p>
            )}
          </div>

          <div>
            <Textarea
              label="Landing Page Text"
              placeholder="Enter text to display on campaign landing pages (HTML tags will be removed)"
              value={landingPageText}
              onChange={(e) => setLandingPageText(e.target.value)}
              minRows={4}
              maxRows={8}
            />
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              This text will appear at the top of campaign landing pages for your organization. HTML tags will be automatically removed.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onPress={dismissModal} variant="light">
            Cancel
          </Button>
          <Button 
            onPress={handleSave} 
            color="primary" 
            isLoading={loading}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Save Landing Page'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LandingPageModal;
